import { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import QuestionSection from "../../ui/QuestionSection/QuestionSection";
import CommonFooter from "../../CommonFooter/CommonFooter";
import CommonButton from "../../ui/CommonButton/CommonButton";
import classes from "./ReagentsConsumables.module.css";
import { useAccreditation } from "../../../hooks/useAccreditation";

const ReagentsConsumables = () => {
  const { labId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const [isEditing, setIsEditing] = useState(false);

  const { formData, setFormData, loading, hydrated, fetching, editable, setEditable, handleDone } =
    useAccreditation({
      labId,
      section: "Reagents Consumables",
      name: "SADCAS F134-B",
      auditorId: user._id,
      isAssessor: user.role === "ASSESSOR",
      isEditing,
      token,
    });

  const handleAnswerChange = (questionId, score, comment, points, content) => {
    setFormData((prev) => ({
      ...prev,
      [questionId]: { score, comment, points, content },
    }));
  };

  const questionSets = [
    {
      questions1: [
        {
          questionId: "Q1",
          content:
            "a. Does the laboratory store reagents and consumables according to the manufacturers’ specifications and monitor the environmental conditions where relevant?",
          number: "6.6.2",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q2",
          content:
            "b. When the laboratory is not the receiving facility, do they verify that the receiving facility has adequate storage and handling capabilities to maintain supplies in a manner that prevents damage and deterioration?",
          number: "6.6.3",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q3",
          content:
            "c. Is new formulation of examination kits with changes in reagents or procedure, or new lot or shipment verified for performance before use or before release of results, as appropriate?",
          number: "6.6.4",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q4",
          content:
            "d. Does the inventory management system established by the laboratory segregate reagents and consumables that have been accepted for use from those that have not been inspected or accepted?",
          number: "6.6.5",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q5",
          content:
            "e. Are instructions for use of reagents and consumables, including those provided by the manufacturers, readily available?",
          number: "6.6.6",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q6",
          content:
            "f. Are adverse incidents and accidents (attributed to specific reagents or consumables) investigated and reported to manufacturer and/or supplier and appropriate authorities, as required?",
          number: "6.6.6",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q7",
          content:
            "g. Does the laboratory have procedures for responding to any manufacturer’s recall or other notice and taking actions recommended by the manufacturer?",
          number: "6.6.7",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          content: "h. Are the following records available:",
          number: "",
          hideOptions: true,
        },
        {
          questionId: "Q8",
          content: "i. Identity of the reagent or consumable",
          number: "",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q9",
          content: "ii. Manufacturer’s name, and batch code/ lot number",
          number: "",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q10",
          content:
            "iii. Date of receipt and condition when received, expiry date, date of first use and where applicable date when taken out of service",
          number: "",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q11",
          content:
            "iv. Records that confirmed the reagent’s or consumable’s initial and ongoing acceptance for use",
          number: "",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
      ],
      questions2: [
        {
          questionId: "Comment1",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
      ],
      render: (onAnswerChange, answers) => (
        <>
          <QuestionSection
            topQuestion={false}
            accreditation
            addWidth
            showPointsField={false}
            questions={questionSets[0].questions1}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
          <QuestionSection
            topQuestion={false}
            showQuestion={false}
            showEmptyNumber={false}
            accreditation
            showPointsField={false}
            questions={questionSets[0].questions2}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
        </>
      ),
    },
  ];

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet((prev) => prev - 1);
  };

  if (!hydrated || fetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}>
      <div className="d-flex justify-content-between align-items-center">
        <div
          className="d-flex align-items-center gap-2 mb-4"
          onClick={() => navigate(-1)}
          role="button"
        >
          <IoIosArrowRoundBack size={35} />
          <h2 className="m-0">Clause 7.2.3: Reagents and consumables</h2>
        </div>

        {user.role === "ASSESSOR" && !editable && (
          <CommonButton
            className={classes.editButton}
            onClick={() => {
              setIsEditing(true);
              setEditable(true);
            }}
            title="Edit"
          />
        )}
      </div>

      {!editable ? (
        <div className={classes.infoText}>
          ✅ Your SADCAS B - Clause 7.2.3: Reagents and consumables has been submitted.
        </div>
      ) : (
        questionSets[currentSet].render(handleAnswerChange, formData)
      )}

      {editable && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => setEditable(false)}
          currentSet={currentSet}
          questionSets={questionSets}
          doneText={loading ? "Submitting..." : "Update"}
          disabled={loading}
        />
      )}
    </div>
  );
};

export default ReagentsConsumables;